/**
 * DateCell Component
 *
 * Renders a date value with optional time.
 * Clicking opens a date picker for selection.
 */

import { useState, useRef, useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import type { DateCellProps } from './types';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function DateCell({
  value,
  onChange,
  includeTime = false,
  // dateFormat is available for future customization
  dateFormat: _dateFormat = 'MMM d',
  readonly = false,
}: DateCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value?.date) {
      return new Date(value.date);
    }
    return new Date();
  });
  const [timeValue, setTimeValue] = useState(value?.time ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const monthIndex = date.getMonth();
    const month = MONTHS[monthIndex] ?? 'Jan';
    const day = date.getDate();
    return `${month.slice(0, 3)} ${day}`;
  };

  const handleDateSelect = (day: number) => {
    if (readonly || !onChange) return;

    const selectedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const dateStr = selectedDate.toISOString().split('T')[0] as string;

    const newValue: { date: string; time?: string } = {
      date: dateStr,
    };
    if (includeTime && timeValue) {
      newValue.time = timeValue;
    }
    onChange(newValue);

    if (!includeTime) {
      setIsOpen(false);
    }
  };

  const handleTimeChange = (time: string) => {
    setTimeValue(time);
    if (value?.date && onChange) {
      onChange({
        date: value.date,
        time: time || undefined,
      });
    }
  };

  const handleClear = () => {
    if (readonly || !onChange) return;
    onChange(null);
    setIsOpen(false);
  };

  const navigateMonth = (delta: number) => {
    setViewDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days: { day: number; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean }[] = [];

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
      });
    }

    // Current month days
    const today = new Date();
    const selectedDate = value?.date ? new Date(value.date) : null;

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = today.getDate() === day &&
        today.getMonth() === month &&
        today.getFullYear() === year;
      const isSelected = selectedDate &&
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === month &&
        selectedDate.getFullYear() === year;

      days.push({
        day,
        isCurrentMonth: true,
        isToday,
        isSelected: !!isSelected,
      });
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
      });
    }

    return days;
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center">
        {value?.date ? (
          <span className="text-sm text-gray-700">
            {formatDate(value.date)}
            {value.time && ` ${value.time}`}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        onClick={() => setIsOpen(true)}
        className="w-full h-full flex items-center cursor-pointer min-h-[28px] text-left"
      >
        {value?.date ? (
          <span className="text-sm text-gray-700">
            {formatDate(value.date)}
            {value.time && ` ${value.time}`}
          </span>
        ) : (
          <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
        )}
      </button>

      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-dropdown border border-surface-border p-3 w-72">
          {/* Header with month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-surface-hover rounded"
            >
              <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-900">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-surface-hover rounded"
            >
              <ChevronRightIcon className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {generateCalendarDays().map((day, index) => (
              <button
                key={index}
                onClick={() => day.isCurrentMonth && handleDateSelect(day.day)}
                disabled={!day.isCurrentMonth}
                className={`
                  p-2 text-sm rounded transition-colors
                  ${day.isCurrentMonth ? 'hover:bg-primary-50' : 'text-gray-300 cursor-default'}
                  ${day.isToday ? 'font-bold text-primary-600' : ''}
                  ${day.isSelected ? 'bg-primary-500 text-white hover:bg-primary-600' : ''}
                `}
              >
                {day.day}
              </button>
            ))}
          </div>

          {/* Time input (if enabled) */}
          {includeTime && (
            <div className="mt-3 pt-3 border-t border-surface-border">
              <label className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Time:</span>
                <input
                  type="time"
                  value={timeValue}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-surface-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="mt-3 pt-3 border-t border-surface-border flex justify-between">
            <button
              onClick={handleClear}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Done
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
}
